import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { CompanyStatus } from "@/graphql/generated/types";

// DEACTIVATING AND REACTIVATING A COMPANY — spark#253, and the exit v9's freeze never had.
//
// genesis has implemented the whole status half of the concept since v7: `Company.status` is
// updatable by an admin of this company, and `deleteCompany` is a soft delete that sets INACTIVE
// (the company entity model — "Löschen und Deaktivieren sind dieselbe Handlung"). spark read none of
// it and offered no control, which left the product telling admins to do something it did not let
// them do: every freeze refusal genesis raises ends in "reactivate the company" (GENESIS001250, and
// spark's own `company.inactive.frozen`), while an inactive company's page looked exactly like an
// active one.
//
// NOT IN THE SAVE-BAR DRAFT. Every other company edit on this page is staged and written by the
// SaveBar. This one is not, because 0006's v7 change note settles it: the status is "kein
// Steckbriefwert, sondern ein Lebenszyklus-Zustand", and the draft is the Steckbrief mechanism.
// (Not §4 — that paragraph is the contact-person cap.) Practically it also
// keeps "deactivate this company" from sharing a Save with "fix a typo in the name".
//
// ONE DIRECTION CONFIRMS, THE OTHER DOES NOT (Kevin, 2026-08-31). Deactivating takes the company out
// of both searches and freezes every post and project in its name, so it is asked twice. Reactivating
// only gives those back — there is nothing to lose by doing it and nothing to warn about — so it
// happens on the click.
//
// WHAT DEACTIVATING IS NOT. It empties nothing: name, industry, logo, size, About us, expertise,
// members and contact persons all stay exactly as they were, and reactivating restores the company
// whole (0001 §1, v7). The confirmation says what it DOES cost rather than asking "are you sure",
// because the two costs are the ones an admin cannot see from here.
export function CompanyStatusControl({
  status,
  onChange,
  busy = false,
}: {
  /** The company's stored status. Null while the profile read has not landed. */
  status: CompanyStatus | null | undefined;
  /** Performs the write. Resolves to a refusal message, or null when it landed. */
  onChange: (next: CompanyStatus) => Promise<string | null>;
  busy?: boolean;
}) {
  const intl = useIntl();
  // ASKED FOR AND NOT YET ANSWERED. Local, and deliberately not lifted: nothing outside this control
  // acts on a half-made decision, and leaving the page is a perfectly good way to abandon one.
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  // Undecided until the read lands. Rendering "Deactivate" against an unknown status would offer the
  // wrong act to an admin of a company that is already inactive.
  if (status == null) return null;

  const inactive = status === CompanyStatus.Inactive;

  async function write(next: CompanyStatus) {
    setWorking(true);
    setMessage(null);
    try {
      setMessage(await onChange(next));
      // The question is spent either way: it was answered, and a refusal is answered by reading the
      // refusal, not by leaving a second Deactivate armed under it.
      setConfirming(false);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* THE STATE IS STATED, not just implied by which button is on offer. An inactive company's
          page is otherwise indistinguishable from an active one — the concept keeps it reachable and
          keeps its content on screen — so an admin who did not deactivate it themselves has no other
          way to find out. Neutral text, because being inactive is a state the company chose, not a
          fault (docs/UI_CONVENTIONS.md). */}
      {inactive ? (
        <p id="company-status-inactive" className="smycles-field-meta text-ink">
          <FormattedMessage
            id="company.admin.status.inactive"
            defaultMessage="This company is deactivated. It is out of both searches, and nothing can be published or changed in its name."
          />
        </p>
      ) : null}

      {inactive ? (
        <Button
          id="company-reactivate"
          variant="chip"
          size="compact"
          disabled={busy || working}
          onClick={() => void write(CompanyStatus.Active)}
        >
          <FormattedMessage
            id="company.admin.status.reactivate"
            defaultMessage="Reactivate company"
          />
        </Button>
      ) : confirming ? (
        <>
          <p id="company-status-confirmhint" className="smycles-field-meta text-ink">
            <FormattedMessage
              id="company.admin.status.confirm"
              defaultMessage="Deactivating takes this company out of both searches and freezes its posts and projects: they can no longer be published, edited or deleted. Nothing is deleted, and reactivating gives all of it back."
            />
          </p>
          <div className="flex items-center gap-2">
            <Button
              id="company-deactivate-confirm"
              variant="destructive"
              size="compact"
              disabled={busy || working}
              onClick={() => void write(CompanyStatus.Inactive)}
            >
              <FormattedMessage
                id="company.admin.status.deactivateConfirm"
                defaultMessage="Deactivate"
              />
            </Button>
            <Button
              id="company-deactivate-cancel"
              variant="chip"
              size="compact"
              disabled={working}
              onClick={() => {
                setConfirming(false);
                setMessage(null);
              }}
            >
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          </div>
        </>
      ) : (
        <Button
          id="company-deactivate"
          variant="chip"
          size="compact"
          disabled={busy || working}
          onClick={() => setConfirming(true)}
        >
          <FormattedMessage
            id="company.admin.status.deactivate"
            defaultMessage="Deactivate company"
          />
        </Button>
      )}

      {message ? <p id="company-status-message" className="smycles-field-meta text-ink">{message}</p> : null}
      {/* An accessible name for the block, so a screen reader lands on "company status" rather than
          on a bare button whose meaning comes from the paragraph above it. */}
      <span className="sr-only">
        {intl.formatMessage({
          id: "company.admin.status.label",
          defaultMessage: "Company status",
        })}
      </span>
    </div>
  );
}
